import { useState, useEffect } from "react";
import axios from "axios";
import { API_CONSTANTS } from "../../config/api-constants";
import "../index.scss";
import "../online-scheduler.scss";
import BookingHeader from "../components/BookingHeader";
import BookingFooter from "../components/BookingFooter";
import BookingProgress from "../components/BookingProgress";
import { Button } from "../components/ui/button";
import toast from "react-hot-toast";
import { format } from "date-fns";
import InspectionAddressForm from "../components/booking/InspectionAddressForm";
import PropertyInfoForm from "../components/booking/PropertyInfoForm";
import ClientInfoForm from "../components/booking/ClientInfoForm";
import SelectServicesForm from "../components/booking/SelectServicesForm";
import DateTimeForm from "../components/booking/DateTimeForm";
import SuccessScreen from "../components/booking/SuccessScreen";
import BookingSummary from "../components/booking/BookingSummary";
import NotFound from "./NotFound";
import { setSupabaseAnonKey } from "../integrations/supabase/client";

const getStorageItem = (storage, key) => {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
};

const resolveCompanyKey = () =>
  getStorageItem(sessionStorage, "company_key") || "";

const resolveCompanyName = () =>
  getStorageItem(sessionStorage, "company_name") || "";

const BookingPage = () => {
  const company = resolveCompanyName();
  const token = resolveCompanyKey();

  const [companyDetails, setCompanyDetails] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [refreshCount, setRefreshCount] = useState(0);

  useEffect(() => {
    const companyKey = resolveCompanyKey();
    if (!companyKey) {
      setNotFound(true);
      return;
    }
    axios
      .post(API_CONSTANTS.GET_COMPANY_DETAILS_FOR_SCHEDULER, { company_key: companyKey })
      .then((res) => {
        setSupabaseAnonKey(res.data?.data?.supabase_key);
        setCompanyDetails(res.data);
      })
      .catch((err) => {
        console.error("Failed to fetch company details:", err);
        if (err?.response?.status === 404) {
          setNotFound(true);
        }
      });
  }, [refreshCount]);

  const [currentStep, setCurrentStep] = useState(0);
  const [localAgents, setLocalAgents] = useState([]);
  const handleAgentCreated = (newAgent) => setLocalAgents((prev) => [...prev, newAgent]);
  const allAgents = [...(companyDetails?.data?.agents || []), ...localAgents];

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.matchMedia("(max-width: 767px)").matches : false
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const totalSteps = isMobile ? 6 : 5;
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isBooking, setIsBooking] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [formData, setFormData] = useState({
    primaryClientId: null,
    requiresNewEmail: false,
    secondaryClientId: null,
    secondaryFirstName: "",
    secondaryLastName: "",
    secondaryEmail: "",
    secondaryPhone: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    gateCode: "",
    propertyNotes: "",
    appointmentDate: null,
    appointmentTime: "",
    propertyType: "",
    additionalNotes: "",
    services: [],
    servicePrices: {},
    serviceDurations: {},
    serviceNames: {},
    preferredInspector: "any",
    availableInspectors: [],
    agentId: null,
    agentType: null,
    secondAgentId: null,
    secondAgentType: null,
    foundationType: "",
    squareFeet: "",
    yearBuilt: "",
    bedrooms: "",
    bathrooms: "",
    hasGarage: null,
  });

  // Warn before tab close / reload when the user has entered any form data
  useEffect(() => {
    const hasFormData = !!(
      formData.firstName ||
      formData.lastName ||
      formData.email ||
      formData.phone ||
      formData.address ||
      formData.services.length > 0 ||
      formData.appointmentDate
    );

    if (!hasFormData || isSubmitted) return;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [formData, isSubmitted]);

  const applyPrimaryClient = (client) => {
    setFormData((prev) => ({
      ...prev,
      primaryClientId: client?.id ?? null,
      firstName: client?.first_name ?? "",
      lastName: client?.last_name ?? "",
      email: client?.email ?? "",
      phone: client?.phone ?? "",
    }));
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next.primaryClientId;
      return next;
    });
  };

  const applySecondaryClient = (client) => {
    setFormData((prev) => ({
      ...prev,
      secondaryClientId: client?.id ?? null,
      secondaryFirstName: client?.first_name ?? "",
      secondaryLastName: client?.last_name ?? "",
      secondaryEmail: client?.email ?? "",
      secondaryPhone: client?.phone ?? "",
    }));
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (validationErrors[field]) {
      setValidationErrors((prev) => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
  };
  const isStepComplete = (step = currentStep) => {
    switch (step) {
      case 0:
        return (
          !formData.requiresNewEmail &&
          !!formData.email &&
          !!formData.firstName &&
          !!formData.lastName &&
          !!formData.phone &&
          /^\d{3}-\d{3}-\d{4}$/.test(formData.phone) &&
          (!formData.agentId || !!formData.agentType) &&
          (!formData.secondAgentId || !!formData.secondAgentType)
        );
      case 1:
        return !!(formData.address && formData.city && formData.state && formData.zipCode);
      case 2:
        return true; // no required fields
      case 3:
        return formData.services.length > 0;
      case 4:
        return !!(formData.appointmentDate && formData.appointmentTime);
      case 5:
        return true; // booking summary review step (mobile only)
      default:
        return true;
    }
  };

  const validateStep = () => {
    const errors = {};
    if (currentStep === 0) {
      if (formData.requiresNewEmail) errors.email = "Enter a new email address to continue.";
      else if (!formData.email) errors.email = "Email is required";
      if (!formData.firstName) errors.firstName = "First name is required";
      if (!formData.lastName) errors.lastName = "Last name is required";
      if (!formData.phone) errors.phone = "Phone number is required";
      else if (!/^\d{3}-\d{3}-\d{4}$/.test(formData.phone))
        errors.phone = "Phone number must be in 123-456-7890 format";
    } else if (currentStep === 1) {
      const isUS = Number(companyDetails?.data?.company_data?.is_from_us ?? 1) === 1;
      if (!formData.address) errors.address = "Address is required";
      if (!formData.city) errors.city = "City is required";
      if (!formData.state)
        errors.state = isUS ? "State is required" : "Province is required";
      if (!formData.zipCode)
        errors.zipCode = isUS ? "ZIP Code is required" : "Postal Code is required";
    } else if (currentStep === 3) {
      if (formData.services.length === 0) errors.services = "At least one service is required";
    } else if (currentStep === 4) {
      if (!formData.appointmentDate) errors.appointmentDate = "Please select a date";
      if (!formData.appointmentTime) errors.appointmentTime = "Please select a time";
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep()) {
      if (currentStep === 3 && formData.services.length === 0) {
        toast.error("Please choose at least one service before scheduling a date and time.");
      } else {
        toast.error("Please fill out all required fields to continue.");
      }
      return;
    }

    // Step 0: if no existing client was selected, create a new one
    if (currentStep === 0 && !formData.primaryClientId) {
      try {
        const payload = {
          company_key: resolveCompanyKey(),
          first_name: formData.firstName.trim(),
          last_name: formData.lastName.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
        };
        const response = await axios.post(API_CONSTANTS.ADD_ONLINE_SCHEDULER_CLIENTS, payload);
        if (response?.data?.success) {
          const returnedId = response.data?.data;
          setFormData((prev) => ({
            ...prev,
            primaryClientId: returnedId || null,
          }));
        } else {
          toast.error(response?.data?.message || "Failed to create client. Please try again.");
          return;
        }
      } catch (err) {
        const msg = err?.response?.data?.message || "Failed to create client. Please try again.";
        toast.error(msg);
        return;
      }
    }

    if (currentStep === totalSteps - 1) {
      try {
        // Parse appointment start and end time from "09:00 AM - 11:30 AM"
        const parseHHMM = (timeToken) => {
          const [tp, mer] = (timeToken || "").trim().split(" ");
          const [rh, rm] = tp.split(":").map(Number);
          let h = rh;
          if (mer?.toUpperCase() === "PM" && h !== 12) h += 12;
          if (mer?.toUpperCase() === "AM" && h === 12) h = 0;
          return { h, m: rm || 0, hhmm: `${String(h).padStart(2, "0")}:${String(rm || 0).padStart(2, "0")}` };
        };
        const [startToken, endToken] = (formData.appointmentTime || "").split(" - ");
        const { h: hour, m: rawMin, hhmm: selectedStartHHMM } = parseHHMM(startToken);
        const { hhmm: selectedEndHHMM } = parseHHMM(endToken);

        const apptDate = formData.appointmentDate ? new Date(formData.appointmentDate) : new Date();
        const localDate = new Date(apptDate.getFullYear(), apptDate.getMonth(), apptDate.getDate(), hour, rawMin || 0, 0);

        const pad = (n) => String(n).padStart(2, "0");
        const formatLocal = (d) =>
          `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
        const formatUTC = (d) =>
          `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;

        const localStartTime = formatLocal(localDate);
        const inspectionDatetime = formatUTC(localDate);
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        // Map services from companyDetails to get ids, durations, template_ids
        const apiServices = companyDetails?.data?.services || [];
        const selectedServiceIds = [];
        const selectedServiceDurations = [];
        const orderTemplates = [];
        const agreementIds = [];

        formData.services.forEach((serviceId) => {
          const svc = apiServices.find((s) => String(s.id) === String(serviceId));
          if (svc) {
            selectedServiceIds.push(svc.id);
            selectedServiceDurations.push(
              Number(formData.serviceDurations?.[serviceId] ?? svc.minutes ?? 0) || 0
            );
            const templates = svc.servicetemplate || [];
            templates.forEach((t) => {
              if (t.template_id) orderTemplates.push(t.template_id);
            });
            if (svc.agreement_id) agreementIds.push(svc.agreement_id);
          }
        });

        const charges = Object.values(formData.servicePrices || {}).reduce((sum, p) => sum + Number(p || 0), 0);
        const duration = Object.values(formData.serviceDurations || {}).reduce((sum, d) => sum + Number(d || 0), 0);

        // Inspector — use selected ID, or first inspector who has the selected slot
        let inspectorId = [];
        if (formData.preferredInspector && formData.preferredInspector !== "any") {
          inspectorId = [Number(formData.preferredInspector)];
        } else {
          const matchingInspector = formData.availableInspectors?.find(
            (insp) => insp.slots?.some((s) => s.start === selectedStartHHMM && s.end === selectedEndHHMM)
          ) || formData.availableInspectors?.[0];
          if (matchingInspector?.id) inspectorId = [Number(matchingInspector.id)];
        }

        // Agents split by type — extract numeric id only
        const extractId = (val) => (val && typeof val === "object" ? val.id ?? val.client_id ?? val.agent_id : val);
        const buyerAgentIds = [];
        const sellerAgentIds = [];
        if (formData.agentId) {
          const id = extractId(formData.agentId);
          if (formData.agentType === "sellers") sellerAgentIds.push(id);
          else buyerAgentIds.push(id);
        }
        if (formData.secondAgentId) {
          const id = extractId(formData.secondAgentId);
          if (formData.secondAgentType === "sellers") sellerAgentIds.push(id);
          else buyerAgentIds.push(id);
        }

        // Client ids — extract numeric id only
        const clientIds = [extractId(formData.primaryClientId)].filter(Boolean);
        if (formData.secondaryClientId) clientIds.push(extractId(formData.secondaryClientId));

        const payload = {
          company_key: resolveCompanyKey(),
          inspection_datetime: inspectionDatetime,
          local_starttime: localStartTime,
          timezone,

          location_address: formData.address,
          location_city: formData.city,
          location_state: formData.state,
          location_zip: formData.zipCode,

          inspector_id: inspectorId,
          client_id: clientIds,
          buyer_agent_id: buyerAgentIds,
          seller_agent_id: sellerAgentIds,

          service_id: selectedServiceIds,
          service_duration: selectedServiceDurations,

          charges,
          duration,

          order_templates: orderTemplates,
          agreement_id: [...new Set(agreementIds)],

          local_agreement_send_time: localStartTime,

          ...(formData.squareFeet && { square_feet: Number(formData.squareFeet) }),
          ...(formData.bedrooms && { total_bedrooms: formData.bedrooms }),
          ...(formData.bathrooms && { total_bathrooms: formData.bathrooms }),
          ...(formData.yearBuilt && { year_built: Number(formData.yearBuilt) }),
          ...(formData.hasGarage !== null && { is_garage_available: formData.hasGarage ? 1 : 0 }),
          ...(formData.gateCode && { gate_code: formData.gateCode }),
          ...(formData.propertyType && { type: formData.propertyType }),
          ...(formData.foundationType && { foundation: formData.foundationType }),
          ...(formData.propertyNotes && { property_additional_info: formData.propertyNotes }),
          ...(formData.additionalNotes && { service_additional_info: formData.additionalNotes }),
        };

        setIsBooking(true);
        const response = await axios.post(API_CONSTANTS.STORE_INSPECTION_DATA, payload);
        if (response?.data?.success) {
          setIsSubmitted(true);
        } else {
          toast.error(response?.data?.message || "Unable to complete booking. Please try again.");
          setIsBooking(false);
        }
      } catch (err) {
        console.error("Booking submission error:", err);
        toast.error(err?.response?.data?.message || "Something went wrong. Please try again.");
        setIsBooking(false);
      }
      return;
    }
    nextStep();
  };
  const nextStep = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };
  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };
  const jumpToStep = (step) => {
    if (step >= 0 && step < totalSteps) {
      // Allow going back freely; block jumping forward past incomplete steps
      if (step > currentStep) {
        for (let s = currentStep; s < step; s++) {
          if (!isStepComplete(s)) return;
        }
      }
      setCurrentStep(step);
      window.scrollTo(0, 0);
    }
  };
  const resetForm = () => {
    setIsSubmitted(false);
    setIsBooking(false);
    setCurrentStep(0);
    setRefreshCount((c) => c + 1);
    setValidationErrors({});
    setFormData({
      primaryClientId: null,
      requiresNewEmail: false,
      secondaryClientId: null,
      secondaryFirstName: "",
      secondaryLastName: "",
      secondaryEmail: "",
      secondaryPhone: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      address: "",
      city: "",
      state: "",
      zipCode: "",
      gateCode: "",
      propertyNotes: "",
      appointmentDate: null,
      appointmentTime: "",
      propertyType: "",
      additionalNotes: "",
      services: [],
      servicePrices: {},
    serviceDurations: {},
    serviceNames: {},
    preferredInspector: "any",
      availableInspectors: [],
      agentId: null,
      agentType: null,
      secondAgentId: null,
      secondAgentType: null,
      foundationType: "",
      squareFeet: "",
      yearBuilt: "",
      bedrooms: "",
      bathrooms: "",
      hasGarage: null,
    });
  };
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <ClientInfoForm
            formData={formData}
            handleFormChange={handleFormChange}
            validationErrors={validationErrors}
            agents={allAgents}
            clients={companyDetails?.data?.clients || []}
            onAgentCreated={handleAgentCreated}
            companyKey={token}
          />
        );
      case 1:
        return (
          <InspectionAddressForm
            formData={formData}
            handleFormChange={handleFormChange}
            validationErrors={validationErrors}
            className="pt-0 pb-[10px] pr-[10px] my-[10px]"
            isFromUs={Number(companyDetails?.data?.company_data?.is_from_us ?? 1) === 1}
          />
        );
      case 2:
        return (
          <PropertyInfoForm
            formData={formData}
            handleFormChange={handleFormChange}
            validationErrors={validationErrors}
          />
        );
      case 3:
        return (
          <SelectServicesForm
            formData={formData}
            handleFormChange={handleFormChange}
            validationErrors={validationErrors}
            apiServices={companyDetails?.data?.services || []}
          />
        );
      case 4:
        return (
          <DateTimeForm
            formData={formData}
            handleFormChange={handleFormChange}
            validationErrors={validationErrors}
            schedulerSettings={companyDetails?.data?.company_scheduler_settings || null}
          />
        );
      case 5:
        return (
          <div className="p-0 md:p-6">
            <BookingSummary
              formData={formData}
              currentStep={currentStep}
              onEditStep={jumpToStep}
              agents={allAgents}
            />
          </div>
        );
      default:
        return null;
    }
  };

  if (notFound) {
    return <NotFound />;
  }

  // Trial company that has hit its inspection limit
  const isTrial = companyDetails?.data?.is_trial === 1;
  const canCreate = companyDetails?.data?.can_create_inspection === true;
  if (isTrial && !canCreate) {
    return (
      <div className="online-scheduler-root min-h-screen flex flex-col bg-gray-50">
        <BookingHeader
          companyLogo={companyDetails?.data?.company_data?.logo}
          companyName={companyDetails?.data?.company_data?.name}
        />
        <main className="flex-1 flex items-center justify-center px-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-10 max-w-lg w-full text-center">
            <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Online Bookings Unavailable</h2>
            <p className="text-gray-600 text-sm leading-relaxed">
              We're currently unable to accept new online inspection bookings. Please contact our office directly for scheduling your inspection.
            </p>
          </div>
        </main>
        <BookingFooter companyName={companyDetails?.data?.company_data?.name} />
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <div className="online-scheduler-root min-h-screen flex flex-col bg-gray-50">
        <BookingHeader companyLogo={companyDetails?.data?.company_data?.logo} companyName={companyDetails?.data?.company_data?.name} />
        <main className="flex-1 container mx-auto px-4 py-8 md:py-12 max-w-7xl">
          <div className="bg-white shadow-sm rounded-lg overflow-hidden">
            <SuccessScreen formData={formData} onReset={resetForm} schedulerSettings={companyDetails?.data?.company_scheduler_settings || null} />
          </div>
        </main>
        <BookingFooter companyName={companyDetails?.data?.company_data?.name} />
      </div>
    );
  }
  return (
    <div className="online-scheduler-root min-h-screen flex flex-col bg-gray-50">
      <BookingHeader companyLogo={companyDetails?.data?.company_data?.logo} companyName={companyDetails?.data?.company_data?.name} />
      <main className="flex-1 container mx-auto px-4 py-8 md:py-12 max-w-7xl">
        <div className="flex flex-col md:flex-row">
          <div className="flex-1">
            <div className="px-6 pt-6 pb-0 bg-white shadow-sm rounded-lg pb-2.5">
              <h1 className="text-2xl md:text-3xl !font-bold !text-gray-900 text-center">
                Book Your Inspection
              </h1>
              <p className="text-gray-500 mt-1 text-center text-base">
                Complete the form below to schedule your inspection appointment
              </p>
              <div className="mt-3">
                <BookingProgress
                  currentStep={currentStep}
                  totalSteps={totalSteps}
                  onStepClick={jumpToStep}
                />
              </div>
            </div>
            <form onSubmit={handleSubmit} className="bg-white shadow-sm rounded-lg mt-[-10px]">
              {renderStepContent()}
              <div className="p-6 border-t border-gray-200 flex justify-between">
                <Button
                  type="button"
                  variant="tertiary"
                  onClick={prevStep}
                  disabled={currentStep === 0}
                >
                  Previous
                </Button>
                {currentStep === totalSteps - 1 ? (
                  <Button type="submit" variant="green" disabled={!isStepComplete() || isBooking}>
                    {companyDetails?.data?.company_scheduler_settings?.mark_as_scheduled === 0
                      ? "Request Inspection"
                      : "Book Appointment"}
                  </Button>
                ) : (
                  <Button type="submit" variant="green" disabled={!isStepComplete()}>
                    Continue
                  </Button>
                )}
              </div>
            </form>
          </div>
          <div className="hidden md:block md:w-80 shrink-0">
            <BookingSummary formData={formData} currentStep={currentStep} onEditStep={jumpToStep} agents={allAgents} />
          </div>
        </div>
      </main>
      <BookingFooter companyName={companyDetails?.data?.company_data?.name} />
    </div>
  );
};
export default BookingPage;
